import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { useScreenSize } from '../hooks/useScreenSize';
import 'swiper/css';
import 'swiper/css/navigation';
import '../styles/naslov.css';

const Home = () => {
  const statsGridRef = useRef(null);
  const { isMobile } = useScreenSize();

  // Hide critical hero when React loads
  useLayoutEffect(() => {
    const criticalHero = document.getElementById('critical-hero');
    if (criticalHero) {
      criticalHero.classList.add('hidden');
    }
  }, []);

  // CSS-only stats animation with Intersection Observer
  useLayoutEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
          // Set CSS custom properties for the numbers
          const statNumbers = entry.target.querySelectorAll('.stat-number');
          statNumbers.forEach(stat => {
            const target = parseInt(stat.getAttribute('data-target'));
            stat.style.setProperty('--target', target);
          });
        }
      });
    }, { threshold: 0.5 });

    if (statsGridRef.current) {
      observer.observe(statsGridRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Google Reviews URL - desktop version
  const GOOGLE_REVIEWS_URL_DESKTOP = "https://www.google.com/search?sca_esv=940aa337a8577638&sxsrf=AE3TifNPw_Ks1KNP_lmLxLXlbpgROHLfdw:1764702467214&q=Konto+Zona+Recenzije&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2Njc2sDA1MDAzMjE1MjM2M7Ew3sDI-IpRxDs_ryRfISo_L1EhKDU5Na8qMyt1EStWYQD-1ENiSAAAAA&rldimm=3730850062452636483&tbm=lcl&hl=hr-HR&sa=X&ved=2ahUKEwjZ9sT9zJ-RAxVmLRAIHWXVAecQ9fQKegQIQhAF&biw=1396&bih=870&dpr=2#lkt=LocalPoiReviews";
  
  // Google Reviews URL - mobile version
  const GOOGLE_REVIEWS_URL_MOBILE = "https://www.google.com/search?sca_esv=940aa337a8577638&rlz=1CDGOYI_enHR1105HR1105&hl=hr&sxsrf=AE3TifO0p6wglIGTVOHLMXiLpS2n8Phgbw:1764707767975&q=osvrti%20za%20konto%20zona&ludocid=3730850062452636483&ibp=gwp%3B0%2C7&sa=X&ved=0CAwQ5foLahcKEwjYj7Td4J-RAxUAAAAAHQAAAAAQBA&biw=393&bih=773&dpr=3#lkt=LocalPoiReviews&arid=Ci9DQUlRQUNvZENodHljRjlvT21aSmVuUmlOWFE1ZFZKdFVrcFFWMHBJUmpKR1JYYxAB&lpg=cid:CgIgAQ%3D%3D";
  
  // Select URL based on device type
  const GOOGLE_REVIEWS_URL = isMobile ? GOOGLE_REVIEWS_URL_MOBILE : GOOGLE_REVIEWS_URL_DESKTOP;

  // Real Google Reviews - Each testimonial links to the Google reviews page
  const testimonials = [
    {
      name: "Dragutin Lipik",
      service: "Individualne instrukcije – Računovodstvo",
      text: "Instruktor je iznimno stručan, strpljiv i jasno objašnjava svaku temu, bez obzira na njezinu složenost. Online predavanja su odlično organizirana, interaktivna i vrlo korisna.",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Nika Milos",
      service: "Individualne instrukcije – Računovodstvo",
      text: "Odličan instruktor iz računovodstva! Jasno objašnjava, strpljiv je i uvijek se trudi da sve bude razumljivo uz životne primjere.",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Borna Varga",
      service: "Individualne instrukcije – Matematika",
      text: "Matematika mi je oduvijek teško pala i malo teže mi ju je bilo samoj učiti. Te sam se odlučila upisati instrukcije u konto zoni. Jako sam zadovoljna jer instruktor veoma dobro objašnjava gradivo, strpljiv je i predstavi probleme na jednostavan način.",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Gabriela Tunic",
      service: "Individualne instrukcije",
      text: "Jako detaljno i stručno objašnjeno gradivo. Marko se maksimalno posveti svima i dodatno motivira i pomaže kod vježbanja zadataka. Zahvaljujući instrukcijama položila sam kolegij koji mi je bio zahtjevan. Sve preporuke!",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Leona Katić",
      service: "Individualne instrukcije",
      text: "Najiskrenija preporuka za instrukcije, predavac pun razumijevanja i strpljenja i stvarno prenosi znanje na jednostavan i brz način. Od potpunog neznanja do petice na ispitu🙂",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Petar Tokić",
      service: "Individualne instrukcije",
      text: "Izvrsne instrukcije, sve je objašnjeno jednostavno i jasno. Odličan pristup i puno strpljenja – stvarno pomaže da se gradivo brzo shvati. Preporuka svima!",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Bruno Cesarec",
      service: "Individualne instrukcije",
      text: "Top instruktor - sve objasni na najjednostavniji način i zna prilagodit tempo. Ima strpljenja za svako pitanje, a i atmosfera je opuštena pa se uz rad može i popričat i našalit. Da mogu, dao bi 6 zvjezdica.",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Lara Antolcic",
      service: "Individualne instrukcije",
      text: "Najbolje instrukcije ikad, stečeno super znanje i pologanje predmeta, Marko je zanimljiva osoba i posvecuje se svakom zasebno kako bi doslo do boljeg utvrdivanja gradiva",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Magdalena Borgudan",
      service: "Individualne instrukcije",
      text: "Tople preporuke za Marka! 🙂 Ima jako puno strpljenja, maksimalno se posveti objašnjavanju i uz primjere iz stvarnog života pojašnjava sve što nije jasno.",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Roman Tintor",
      service: "Individualne instrukcije",
      text: "Brzo, jasno i lakopamtljivo pojašnjavanje. Kolega zna o čemu priča te ne gubi vrijeme na glupostima. Daje konkretne primjere te uspoređuje s ispitima. 10/10",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Martin Salamon",
      service: "Individualne instrukcije",
      text: "Jako dobar instruktor, pomaze da se uci s razumijevanjem, nakon instrukcija osoba odlazi sa znanjem nekog predmeta, a ne samo strebanjem na pamet koje ispari nakon jednog dana, sve preporuke.",
      reviewUrl: GOOGLE_REVIEWS_URL
    },
    {
      name: "Sara Kranjcec",
      service: "Individualne instrukcije",
      text: "Odlican instruktor, vrlo jasne upute i korisni zadatci za vjezbanje, pomogo mi je da prodem godinu, sve preporuke🤗",
      reviewUrl: GOOGLE_REVIEWS_URL
    }
  ];

  return (
    <>
      <Helmet>
        <title>Instrukcije Računovodstvo Zagreb | Instrukcije Ekonomija | Konto Zona</title>
        <meta name="description" content="Instrukcije iz računovodstva, ekonomije, statistike i matematike za studente i srednjoškolce. Podrška za EFZG, EFRI, EFST i EFOS. Rezervirajte termin." />
        <meta name="keywords" content="instrukcije, instrukcije računovodstvo, instrukcije računovodstvo Zagreb, instrukcije ekonomija, instrukcije ekonomija Zagreb, instrukcije statistika, instrukcije matematika, online instrukcije, grupne instrukcije, individualne instrukcije, instrukcije za studente, instrukcije za srednjoškolce, instrukcije EFZG, instrukcije EFRI, instrukcije EFST, instrukcije EFOS, najbolje instrukcije računovodstvo, instrukcije računovodstvo cijena, instrukcije osnove računovodstva, instrukcije bilanca, instrukcije račun dobiti i gubitka, instrukcije knjigovodstvo, instrukcije gospodarska statistika, instrukcije gospodarska matematika, instrukcije mikroekonomija, instrukcije makroekonomija" />
        <meta property="og:title" content="Instrukcije Računovodstvo Zagreb | Instrukcije Ekonomija | Konto Zona" />
        <meta property="og:description" content="Instrukcije računovodstvo, instrukcije ekonomija, instrukcije statistika i instrukcije matematika. Individualne i grupne instrukcije online i uživo za studente i srednjoškolce u Zagrebu." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://instrukcijekontozona.hr" />
        <meta property="og:image" content="https://instrukcijekontozona.hr/kontozona-logo.png" />
        <meta property="og:locale" content="hr_HR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Instrukcije Računovodstvo Zagreb | Instrukcije Ekonomija | Konto Zona" />
        <meta name="twitter:description" content="Instrukcije računovodstvo, instrukcije ekonomija, instrukcije statistika i instrukcije matematika. Individualne i grupne instrukcije online i uživo za studente i srednjoškolce u Zagrebu." />
        <meta name="twitter:image" content="https://instrukcijekontozona.hr/kontozona-logo.png" />
        <link rel="canonical" href="https://instrukcijekontozona.hr" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            "name": "Konto Zona",
            "alternateName": "Instrukcije Konto Zona",
            "description": "Instrukcije računovodstvo, ekonomija, statistika i matematika za studente i srednjoškolce u Zagrebu",
            "url": "https://instrukcijekontozona.hr",
            "logo": "https://instrukcijekontozona.hr/kontozona-logo.png",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Diljska ulica 26",
              "addressLocality": "Zagreb",
              "addressCountry": "HR"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+385-97-666-6639",
              "contactType": "customer service",
              "email": "konto.zona@gmail.com",
              "availableLanguage": "hr"
            },
            "sameAs": [
              "https://www.facebook.com/profile.php?id=61584401916262",
              "https://www.instagram.com/konto.zona/"
            ],
            "offers": {
              "@type": "Offer",
              "name": "Individualne instrukcije",
              "price": "18",
              "priceCurrency": "EUR",
              "description": "Individualne instrukcije iz računovodstva, ekonomije, statistike i matematike"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Konto Zona",
            "image": "https://instrukcijekontozona.hr/kontozona-logo.png",
            "description": "Instrukcije računovodstvo, instrukcije ekonomija, instrukcije statistika i instrukcije matematika u Zagrebu",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Diljska ulica 26",
              "addressLocality": "Zagreb",
              "addressCountry": "HR"
            },
            "telephone": "+385-97-666-6639",
            "email": "konto.zona@gmail.com",
            "priceRange": "12€ - 18€",
            "areaServed": {
              "@type": "City",
              "name": "Zagreb"
            }
          })}
        </script>
      </Helmet>
      {/* SEKCIJA 1: HERO */}
      <section 
        className="section hero-final hero-with-background"
        style={{
          backgroundImage: `url(/webp/${isMobile ? 'mobile' : 'desktop'}/Contact6.webp)`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundAttachment: 'scroll',
          minHeight: 'calc(100vh - var(--header-height))',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          paddingTop: '10px'
        }}
      >
        <div className="container text-center">
          <h1 className="hero-main-title">Instrukcije | Skripte</h1>
          <div className="hero-tagline-container">

          </div>
          <h2 className="hero-subtitle">Za studente ekonomije i srednjoškolce koji žele više od prosjeka.</h2>
          
          {/* Statistike kartice u hero sekciji */}
          <div className="hero-stats-grid" ref={statsGridRef}>
            <div className="stat-card hero-stat-card">
              <i className="fas fa-history"></i>
              <h3><span className="stat-number" data-target="1" style={{ color: 'white', '--target': '1' }}></span>+</h3>
              <p style={{ color: 'white' }}>godina iskustva</p>
            </div>
            <div className="stat-card hero-stat-card">
              <i className="fas fa-smile-beam"></i>
              <h3><span className="stat-number" data-target="98" style={{ color: 'white', '--target': '98' }}></span>%</h3>
              <p style={{ color: 'white' }}>zadovoljnih klijenata</p>
            </div>
            <div className="stat-card hero-stat-card">
              <i className="fas fa-chalkboard-teacher"></i>
              <h3><span className="stat-number" data-target="1000" style={{ color: 'white', '--target': '1000' }}></span>+</h3>
              <p style={{ color: 'white' }}>odrađenih sati</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* SEKCIJA 3: USLUGE */}
      <section className="section">
        <div className="container">
          <div className="section-title">
            <h2>Pronađi svoj put do uspjeha</h2>
            <p>Tri rješenja stvorena za tvoje potrebe. Bilo da voliš raditi sam, u timu, ili uz pomoć pametnih materijala – pokrili smo te.</p>
          </div>
          <div className="grid-3 services-grid">
            <div className="service-card blue-first">
              <div className="service-icon"><i className="fas fa-user-tie"></i></div>
              <h3>Individualne instrukcije</h3>
              <p>Dominiraj na ispitu. Potpuna posvećenost i plan krojen samo za tvoju pobjedu.</p>
              <Link to="/instrukcije#individualne" className="btn btn-blue-first">Istraži predmete</Link>
            </div>
            <div className="service-card red-second">
              <div className="service-icon"><i className="fas fa-book-open"></i></div>
              <h3>Skripte</h3>
              <p>Tvoje tajno oružje. Informacije koje drugi nemaju, stvoreno da napravi razliku na dan ispita.</p>
              <Link to="/skripte" className="btn btn-red-second">Želim skriptu!</Link>
            </div>
            <div className="service-card green-third">
              <div className="service-icon"><i className="fas fa-users"></i></div>
              <h3>Grupne instrukcije</h3>
              <p>Okupi svoju ekipu i podijelite trošak. Logično, zar ne?</p>
              <Link to="/instrukcije#grupne" className="btn btn-green-third">Grupni paketi</Link>
            </div>
          </div>
        </div>
      </section>

      {/* SEKCIJA 4: PREDNOSTI */}
      <section className="section section-alt-bg">
        <div className="container">
          <div className="section-title">
            <h2>Uči pametnije, ne teže.</h2>
            <p>Online instrukcije nisu samo budućnost, one su i bolje. Evo zašto.</p>
          </div>
          <div className="grid-3 services-grid">
            <div className="service-card service-card-1">
              <div className="service-icon express-icon"><i className="far fa-calendar-check"></i></div>
              <h3>Fleksibilnost</h3>
              <p>Tvoj raspored, tvoja pravila. Usklađujemo se s tvojim obavezama.</p>
            </div>
            <div className="service-card service-card-2">
              <div className="service-icon express-icon"><i className="fas fa-history"></i></div>
              <h3>Ušteda vremena</h3>
              <p>Pretvori vrijeme iz gradske gužve u dodatno znanje, ili jednostavno – u vrijeme za sebe.</p>
            </div>
            <div className="service-card service-card-3">
              <div className="service-icon express-icon"><i className="fas fa-mouse-pointer"></i></div>
              <h3>Efikasnost</h3>
              <p>Gubljenje vremena na listanje? Mi smo uveli novo pravilo. Jedan ekran, zajednički fokus.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEKCIJA 5: KOMENTARI */}
      <section className="section testimonials-section">
        <div className="container">
          <div className="section-title">
            <h2>Što kažu naši klijenti</h2>
          </div>
          <div className="testimonials-wrapper">
            <div className="swiper-button-prev"></div>
            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={30}
              slidesPerView={1}
              loop={true}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              breakpoints={{
                768: {
                  slidesPerView: 2,
                },
                1024: {
                  slidesPerView: 3,
                }
              }}
              className="testimonials-slider"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <a 
                    href={testimonial.reviewUrl || GOOGLE_REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="testimonial-card-link"
                    style={{ textDecoration: 'none', display: 'block' }}
                  >
                  <article className="testimonial-card">
                    <p className="testimonial-text">"{testimonial.text}"</p>
                    <h4 className="testimonial-name">{testimonial.name}</h4>
                    <p className="testimonial-service">{testimonial.service}</p>
                  </article>
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swiper-button-next"></div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home; 